import React from "react";
import { Box, Skeleton, Tooltip, Typography, alpha, useTheme } from "@mui/material";

// Online is a status ("good"), offline a neutral: colors checked with the dataviz validator
// against the card surface in each mode (dark #21295c, light #f0f0f0).
const COLORS = {
  dark: { online: "#43a047", offline: "#8a8fab" },
  light: { online: "#2e7d32", offline: "#858585" },
};

const pct = (value, total) => (total > 0 ? (value / total) * 100 : 0);

// One part of the split bar: the hit area is taller than the 8px mark so it is easy to hover
const Segment = ({ width, color, tip }) => (
  <Tooltip title={tip} arrow placement="top">
    <Box sx={{ width: `${width}%`, minWidth: 6, py: "6px", cursor: "default" }}>
      <Box
        sx={{
          height: 8,
          borderRadius: "4px",
          backgroundColor: color,
          transition: "width 0.4s ease",
        }}
      />
    </Box>
  </Tooltip>
);

const OnlineStateChart = ({ title, icon, unit = "", data, total }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const colors = COLORS[isDark ? "dark" : "light"];
  const accent = theme.palette.secondary.main;
  const valueColor = theme.palette.secondary[200];   // same gold as the StatBox values

  const header = (right) => (
    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
      <Box display="flex" alignItems="center" gap={1} minWidth={0}>
        {icon && (
          <Box
            sx={{
              width: 30,
              height: 30,
              flexShrink: 0,
              borderRadius: 1.5,
              display: "grid",
              placeItems: "center",
              color: accent,
              backgroundColor: alpha(accent, isDark ? 0.12 : 0.18),
              "& svg": { fontSize: 18 },
            }}
          >
            {icon}
          </Box>
        )}
        <Typography variant="h6" noWrap sx={{ fontWeight: 600, color: theme.palette.secondary[100] }}>
          {title}
        </Typography>
      </Box>
      {right}
    </Box>
  );

  // Loading
  if (!data) {
    return (
      <Box display="flex" flexDirection="column" height="100%" gap={1.5}>
        {header(null)}
        <Skeleton variant="text" width="45%" height={44} />
        <Skeleton variant="rounded" height={8} sx={{ mt: "auto", mb: "6px" }} />
      </Box>
    );
  }

  const find = (label) => data.find((item) => item.label?.toLowerCase() === label)?.value || 0;
  const online = find("online");
  const offline = find("offline");
  const chartTotal = total ?? online + offline;

  const onlinePct = pct(online, chartTotal);
  const offlinePct = pct(offline, chartTotal);
  const noun = unit ? ` ${unit}` : "";

  return (
    <Box display="flex" flexDirection="column" height="100%">
      {header(
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, whiteSpace: "nowrap" }}>
          {chartTotal} total
        </Typography>
      )}

      {/* Headline: online count + share, offline beside it */}
      <Box display="flex" alignItems="flex-end" justifyContent="space-between" gap={2} mt="auto">
        <Box minWidth={0}>
          <Box display="flex" alignItems="center" gap={0.75}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: colors.online,
                boxShadow: `0 0 0 3px ${alpha(colors.online, 0.25)}`,
                ...(online > 0 && {
                  animation: "onlinePulse 2s ease-in-out infinite",
                  "@keyframes onlinePulse": {
                    "0%, 100%": { boxShadow: `0 0 0 3px ${alpha(colors.online, 0.25)}` },
                    "50%": { boxShadow: `0 0 0 5px ${alpha(colors.online, 0.08)}` },
                  },
                  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                }),
              }}
            />
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
              Online
            </Typography>
          </Box>

          <Box display="flex" alignItems="baseline" gap={0.75}>
            <Typography
              sx={{ fontSize: "1.9rem", lineHeight: 1.15, fontWeight: 700, color: valueColor }}
            >
              {online}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
              {Math.round(onlinePct)}%
            </Typography>
          </Box>
        </Box>

        <Box textAlign="right" flexShrink={0}>
          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.75}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
              Offline
            </Typography>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: colors.offline }} />
          </Box>
          <Typography
            sx={{ fontSize: "1.25rem", lineHeight: 1.4, fontWeight: 600, color: valueColor }}
          >
            {offline}
          </Typography>
        </Box>
      </Box>

      {/* Split bar: 2px surface gap between the parts, empty track for anything not reported */}
      <Box
        role="img"
        aria-label={`${online} of ${chartTotal}${noun} online, ${offline} offline`}
        sx={{
          display: "flex",
          gap: "2px",
          mt: 0.5,
          borderRadius: "4px",
          backgroundImage: `linear-gradient(${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"}, ${
            isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"
          })`,
          backgroundSize: "100% 8px",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {chartTotal === 0 ? (
          <Box sx={{ height: 20, width: "100%" }} />
        ) : (
          <>
            {online > 0 && (
              <Segment
                width={onlinePct}
                color={colors.online}
                tip={`Online: ${online} of ${chartTotal}${noun} (${Math.round(onlinePct)}%)`}
              />
            )}
            {offline > 0 && (
              <Segment
                width={offlinePct}
                color={colors.offline}
                tip={`Offline: ${offline} of ${chartTotal}${noun} (${Math.round(offlinePct)}%)`}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default OnlineStateChart;
